import { FC, ReactElement, useEffect, useRef, useState } from 'react';
import CounterpartyTables from './components/CounterpartyTables';
import { useDispatch } from 'react-redux';
import {
  addAdditionalTitleBarComponents,
  removeTitleBarComponent,
  toggleTitleBarLoading,
} from '../../../../redux/pages/actions';
import { Select } from '../../../../types/redux/pages/PagesStore';
import {
  APPEND_NEW_SELECT_TO_TITLEBAR,
  REMOVE_SELECT_FROM_TITLEBAR,
} from '../../../../types/redux/pages/pageTypes';
import useTitleBarSelect from '../../../../hooks/useTitleBarSelect';
import DisplayAreaCenteredWrapper from '../../../layout/utilities/displayAreaWrapper';
import { mainColors } from '../../../../styling/theme';
import { Typography } from '@mui/material';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useActiveCounterpartyAgg } from './queries';
import { keepPreviousData } from '@tanstack/react-query';

interface CounterpartyContainerProps {
  fundId: string;
  positionDate: string | undefined;
  fundName?: string;
  counterpartyOptions: any[];
}

function CounterpartyContainer(
  props: CounterpartyContainerProps,
): ReactElement {
  const { fundId, positionDate, fundName, counterpartyOptions } = props;

  return (
    <CounterpartyTables
      counterpartyOptions={counterpartyOptions}
      positionDate={positionDate || null}
      selectedFund={fundId}
      fundName={fundName}
    />
  );
}

const CounterpartyPage: FC<FundInfoComponentProps> = ({
  fundId,
  positionDate,
  fundName,
}) => {
  const [counterpartyOptions, setCounterpartyOptions] = useState<any[] | null>(
    null,
  );
  const dispatch = useDispatch();
  const selectedCounterparty = useTitleBarSelect('counterpartySelection');
  const dropdownInitializedRef = useRef(false);
  const previousSelectedCounterpartyRef = useRef<string | null>(null);

  const {
    data: activeCounterpartyAgg,
    isLoading,
    isFetching,
    error,
  } = useActiveCounterpartyAgg(fundId, positionDate, {
    placeholderData: keepPreviousData, // Keep previous options visible during refetch
  });

  // Always update options when data is available (even if it's previous data from keepPreviousData)
  useEffect(() => {
    if (activeCounterpartyAgg) {
      // activeCounterpartyAgg is an array of objects directly: [{counterparty_name: "...", counterparty_id: "..."}, ...]
      const options = Array.isArray(activeCounterpartyAgg)
        ? activeCounterpartyAgg.map((item: any) => ({
          label: item.counterparty_name,
          value: item.counterparty_id,
        }))
        : [];

      setCounterpartyOptions(options);
    }
  }, [activeCounterpartyAgg]);

  // Track the currently selected counterparty
  useEffect(() => {
    if (selectedCounterparty) {
      previousSelectedCounterpartyRef.current = selectedCounterparty;
    }
  }, [selectedCounterparty]);


  // Initialize dropdown as soon as we have options - even from the previous data
  useEffect(() => {
    if (counterpartyOptions && counterpartyOptions.length > 0 && !dropdownInitializedRef.current) {
      // Don't set a default - require user selection
      const currentValue = selectedCounterparty || 'none_selected';

      const selectsToAdd: Select[] = [
        {
          titleBarTitle: 'Counterparty',
          titleBarKey: 'counterpartySelection',
          values: counterpartyOptions,
          displayOnOverviewPage: false,
          aValueIsAlwaysSelected: false,
          currentValue: currentValue,
        },
      ];
      dispatch(
        addAdditionalTitleBarComponents(
          selectsToAdd,
          APPEND_NEW_SELECT_TO_TITLEBAR,
        ),
      );
      dispatch(toggleTitleBarLoading(false));
      dropdownInitializedRef.current = true;
    }

    return () => {
      if (dropdownInitializedRef.current) {
        dispatch(
          removeTitleBarComponent('counterpartySelection', REMOVE_SELECT_FROM_TITLEBAR),
        );
        dropdownInitializedRef.current = false;
      }
    };
  }, [counterpartyOptions, selectedCounterparty, isLoading, isFetching, dispatch]);

  // Update selector when data changes (refetch) - preserve selection if it exists in new data
  // Never default to first option - only preserve existing selection or show placeholder
  useEffect(() => {
    if (
      dropdownInitializedRef.current &&
      counterpartyOptions &&
      counterpartyOptions.length > 0
    ) {
      // Check if current selection exists in new data
      const currentSelection = selectedCounterparty;
      const currentSelectionExists = currentSelection && currentSelection !== 'none_selected'
        ? counterpartyOptions.some((opt) => opt.value === currentSelection)
        : false;

      // Only preserve selection if it exists in new data, otherwise show placeholder
      // Never default to first option
      const currentValue = currentSelectionExists
        ? currentSelection!
        : 'none_selected';

      // Update ref only if we have a valid selection (not placeholder)
      if (currentValue && currentValue !== 'none_selected') {
        previousSelectedCounterpartyRef.current = currentValue;
      } else {
        // Clear ref if selection is no longer valid
        previousSelectedCounterpartyRef.current = null;
      }

      const selectsToAdd: Select[] = [
        {
          titleBarTitle: 'Counterparty',
          titleBarKey: 'counterpartySelection',
          values: counterpartyOptions,
          displayOnOverviewPage: false,
          aValueIsAlwaysSelected: false, // Don't require a selection
          currentValue: currentValue,
        },
      ];
      dispatch(
        addAdditionalTitleBarComponents(
          selectsToAdd,
          APPEND_NEW_SELECT_TO_TITLEBAR,
        ),
      );
    }

    return () => {
      dispatch(
        removeTitleBarComponent(
          'counterpartySelection',
          REMOVE_SELECT_FROM_TITLEBAR,
        ),
      );
    };
  }, [counterpartyOptions, selectedCounterparty, isLoading, isFetching, dispatch]);

  return (
    <QueryBoundary
      isLoading={isLoading}
      isFetching={isFetching}
      isError={!!error}
      data={activeCounterpartyAgg}
      loadingMessages={[
        'Loading Counterparties...',
        'This may take a few moments...',
      ]}
      errorMessage="No counterparty data available"
      noDataMessage="No counterparty data available"
    >
      {() => {

        // Show message if no counterparty is selected
        if (!selectedCounterparty || selectedCounterparty === 'none_selected') {
          return (
            <DisplayAreaCenteredWrapper>
              <Typography variant="h6" style={{ color: mainColors.mainBlue }}>
                Please select a counterparty
              </Typography>
            </DisplayAreaCenteredWrapper>
          );
        }
        return (
          <CounterpartyContainer
            fundId={fundId}
            positionDate={positionDate}
            fundName={fundName}
            counterpartyOptions={counterpartyOptions || []}
          />
        );
      }}
    </QueryBoundary>
  );
};

export default CounterpartyPage;
